import React, { useState } from "react";
import {
  relationshipLevelSelector,
} from "@app-redux/selectors";
import { useAppDispatch, useAppSelector } from "@app-redux/store";
import { apiUpdateRelationshipLevel } from "@api/relationshipLevel";
import { updateProfileRelationshipLevel } from "@app-redux/slices/profileSlice";
import { updateProfileListRelationshipLevel } from "@app-redux/slices/profileListSlice";
import { updateRepTop30RelationshipLevel } from "@app-redux/slices/repTop30Slice";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import styles from "./styles.module.css";

type Props = {
  profileId: string;
  currentLevel: number | null | undefined;
  onLevelChanged?: () => void;
};

export const RelationshipLevelSelect = ({
  profileId,
  currentLevel,
  onLevelChanged,
}: Props) => {
  const dispatch = useAppDispatch();
  const { data: levels } = useAppSelector(relationshipLevelSelector);
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentLevelData = levels.find((l) => l.level === currentLevel);

  const handleSelect = async (newLevel: number | null) => {
    setAnchorEl(null);
    if (newLevel === currentLevel) return;
    setSaving(true);
    try {
      await apiUpdateRelationshipLevel(profileId, newLevel);
      dispatch(updateProfileRelationshipLevel({ id: profileId, level: newLevel }));
      dispatch(updateProfileListRelationshipLevel({ id: profileId, level: newLevel }));
      dispatch(updateRepTop30RelationshipLevel({ id: profileId, level: newLevel }));
      onLevelChanged?.();
    } catch {
      // silently fail — value stays as before
    } finally {
      setSaving(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={saving}
        className={styles.triggerButton}
      >
        {currentLevelData
          ? `${currentLevelData.level} - ${currentLevelData.title}`
          : <span className={styles.placeholder}>Set level…</span>}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <table className={styles.levelTable}>
          <thead>
            <tr>
              <th>Level</th>
              <th>Title</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr
              className={`${styles.levelRow} ${currentLevel == null ? styles.selected : ""}`}
              onClick={() => handleSelect(null)}
            >
              <td colSpan={3}><em>None</em></td>
            </tr>
            {levels.map((level) => (
              <tr
                key={level.level}
                className={`${styles.levelRow} ${currentLevel === level.level ? styles.selected : ""}`}
                onClick={() => handleSelect(level.level)}
              >
                <td className={styles.levelNum}>{level.level}</td>
                <td className={styles.levelTitle}>{level.title}</td>
                <td className={styles.levelDesc}>{level.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Popover>
    </>
  );
};
